import React, { useState, useEffect } from "react"
import { graphql, Link, useStaticQuery } from "gatsby"
import { useLocation } from "@reach/router"
import { AnchorLink } from "gatsby-plugin-anchor-links"
import { useTranslation } from "react-i18next"
import { analyticsAction } from "/src/utils/analytics"
import { useLangPath } from "../i18n/useLangPath"

import Logo from "../components/Logo"

function Footer() {
  const { t } = useTranslation()
  const langPath = useLangPath()
  const siteSettings = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)
  const { pathname } = useLocation()
  const url = pathname === "/service/" ? pathname : "/"
  //const { siteUrl } = siteSettings.site.siteMetadata

  const [showConsent, setShowConsent] = useState(false)
  useEffect(() => {
    // Termly sets displayPreferenceModal only when consent banner is active for this region
    let attempts = 0
    let timer = null
    const check = () => {
      if (typeof window.displayPreferenceModal === "function") {
        setShowConsent(true)
      } else if (attempts < 5) {
        attempts++
        timer = setTimeout(check, 1000)
      }
    }
    check()
    return () => { if (timer) clearTimeout(timer) }
  }, [])

  return (
    <footer id="contact">
      <div className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Top area: Blocks */}
          <div className="grid md:grid-cols-12 gap-8 lg:gap-20 mb-8 md:mb-12 pt-6 md:pt-10 border-t border-gray-800">
            {/* 1st block */}
            <div className="md:col-span-3 lg:col-span-3">
              <div className="mb-2">
                {/* Logo */}
                <AnchorLink
                  stripHash={true}
                  to={`${langPath(url)}#home`}
                  className="inline-block"
                  aria-label="Filmustage"
                  onClick={() => analyticsAction("landing_home_click")}
                >
                  <Logo />
                </AnchorLink>
              </div>

              {/* Additional footer text */}
              <div className="text-gray-400">
                <div className="h6 font-bold">{t("footer.headquarters")}</div>
                <div>{t("footer.location")}</div>
                <div>
                  <a
                    href="mailto:hello@filmustage.com?subject=Message%20from%20site"
                    className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                    onClick={() => analyticsAction("landing_mail_click")}
                  >
                    hello@filmustage.com
                  </a>
                </div>
                {/* <div>+1 213.262.0592</div>*/}

                {/*<div className="mt-6">
                  <Link
                    to="/contacts/"
                    className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out underline"
                    onClick={() =>
                      analyticsAction("landing_terms_click")
                    }
                  >
                    Regional Representatives
                  </Link>
                </div>*/}
              </div>
            </div>

            {/* 2nd, 3rd and 4th blocks */}
            <div className="md:col-span-9 lg:col-span-9 grid md:grid-cols-4 grid-cols-2 md:gap-2 gap-8 md:justify-items-center justify-items-left">
              {/* 2nd block */}
              <div className="sm:text-sm">
                <div className="h6 text-gray-200 font-medium mb-1">{t("footer.legals")}</div>
                <ul>
                  <li className="mb-1">
                    <Link
                      to="/terms/"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_terms_click")}
                    >
                      {t("footer.termsOfService")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to="/privacy/"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_privacy_click")}
                    >
                      {t("footer.privacyPolicy")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to="/content-policy/"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_content_policy_click")
                      }
                    >
                      {t("footer.contentPolicy")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to="/cookie-policy/"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_cookie_policy_click")}
                    >
                      {t("footer.cookiePolicy")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to="/referral-program-agreement/"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_referral_agreement_click")
                      }
                    >
                      {t("footer.referralAgreement")}
                    </Link>
                  </li>
                  {showConsent && (
                    <li className="mb-1">
                      <a
                        href="#"
                        className="termly-display-preferences text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                        onClick={e => {
                          e.preventDefault()
                          if (window.displayPreferenceModal) {
                            window.displayPreferenceModal()
                          }
                        }}
                      >
                        {t("footer.consentPreferences")}
                      </a>
                    </li>
                  )}
                </ul>
              </div>

              {/* 3rd block */}
              <div className="sm:text-sm">
                <div className="h6 text-gray-200 font-medium mb-1">
                  {t("footer.resources")}
                </div>
                <ul>
                  <li className="mb-1">
                    <Link
                      to={langPath("/referral-program/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_referral_program_click")
                      }
                    >
                      {t("footer.referralProgram")}
                    </Link>
                  </li>

                  <li className="mb-1">
                    <Link
                      to={langPath("/events/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_webinars_click")}
                    >
                      {t("footer.webinarsEvents")}
                    </Link>
                  </li>

                  <li className="mb-1">
                    <Link
                      to="/blog/tag/templates/"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_templates_click")}
                    >
                      {t("footer.templates")}
                    </Link>
                  </li>

                  <li className="mb-1">
                    <Link
                      to="/blog/tag/guides/"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_guides_click")}
                    >
                      {t("footer.guides")}
                    </Link>
                  </li>

                  <li className="mb-1">
                    <Link
                      to={langPath("/enterprise/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_enterprise_click")
                      }
                    >
                      {t("footer.enterprise")}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* 4th block */}
              <div className="sm:text-sm">
                <div className="h6 text-gray-200 font-medium mb-1">
                  {t("footer.vsOthers")}
                </div>
                <ul>
                  <li className="mb-1">
                    <Link
                      to={langPath("/filmustage-as-an-alternative-to-celtx/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_vs_celtx_click")}
                    >
                      {t("footer.vsCeltx")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={langPath("/filmustage-as-an-alternative-to-yamdu/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_vs_yamdu_click")}
                    >
                      {t("footer.vsYamdu")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={langPath("/filmustage-as-an-alternative-to-gorilla-scheduling/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_vs_gorilla_click")
                      }
                    >
                      {t("footer.vsGorilla")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={langPath("/filmustage-as-an-alternative-to-studiobinder/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_vs_studiobinder_click")
                      }
                    >
                      {t("footer.vsStudioBinder")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={langPath("/filmustage-as-an-alternative-to-movie-magic/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_vs_magic_click")}
                    >
                      {t("footer.vsMovieMagic")}
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={langPath("/filmustage-as-an-alternative-to-scriptation/")}
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_vs_scriptation_click")
                      }
                    >
                      {t("footer.vsScriptation")}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* 5th block */}
              <div className="sm:text-sm">
                <div className="h6 text-gray-200 font-medium mb-1">{t("footer.support")}</div>
                <ul>
                  <li className="mb-1">
                    <a
                      href="https://help.filmustage.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() =>
                        analyticsAction("landing_help_center_click")
                      }
                    >
                      {t("footer.helpCenter")}
                    </a>
                  </li>
                  <li className="mb-1">
                    <a
                      href="#chat"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={e => {
                        e.preventDefault()
                        window.Intercom("show")
                        analyticsAction("landing_chat_click")
                      }}
                    >
                      {t("footer.supportChat")}
                    </a>
                  </li>
                  <li className="mb-1">
                    <a
                      href="mailto:hello@filmustage.com?subject=Message%20from%20site"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_mail_click")}
                    >
                      {t("footer.sendEmail")}
                    </a>
                  </li>
                  <li className="mb-1">
                    <a
                      href="https://filmustage.instatus.com/"
                      target="_blank"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_status_click")}
                    >
                      {t("footer.statusPage")}
                    </a>
                  </li>
                  <li className="mb-1">
                    <a
                      href="https://help.filmustage.com/en/articles/5543878-filmustage-security"
                      target="_blank"
                      className="text-gray-400 hover:text-gray-100 transition duration-150 ease-in-out"
                      onClick={() => analyticsAction("landing_security_click")}
                    >
                      {t("footer.securityPage")}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom area */}
          <div className="md:flex md:items-center md:justify-between">
            {/* Social links */}
            <ul className="flex mb-4 md:order-1 md:ml-4 md:mb-0">
              <li>
                <a
                  href="https://x.com/filmustage/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex justify-center items-center text-purple-600 bg-gray-800 hover:text-gray-100 hover:bg-purple-600 rounded-full transition duration-150 ease-in-out"
                  aria-label="Twitter"
                  onClick={() => analyticsAction("landing_twitter_click")}
                >
                  <svg
                    className="w-8 h-8 fill-current"
                    viewBox="0 0 32 32"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="m13.063 9 3.495 4.475L20.601 9h2.454l-5.359 5.931L24 23h-4.938l-3.866-4.893L10.771 23H8.316l5.735-6.342L8 9h5.063Zm-.74 1.347h-1.457l8.875 11.232h1.36l-8.778-11.232Z"></path>
                  </svg>
                </a>
              </li>
              <li className="ml-4">
                <a
                  href="https://www.facebook.com/filmustage/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex justify-center items-center text-purple-600 bg-gray-800 hover:text-gray-100 hover:bg-purple-600 rounded-full transition duration-150 ease-in-out"
                  aria-label="Facebook"
                  onClick={() => analyticsAction("landing_facebook_click")}
                >
                  <svg
                    className="w-8 h-8 fill-current"
                    viewBox="0 0 32 32"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M14.023 24L14 17h-3v-3h3v-2c0-2.7 1.672-4 4.08-4 1.153 0 2.144.086 2.433.124v2.821h-1.67c-1.31 0-1.563.623-1.563 1.536V14H21l-1 3h-2.72v7h-3.257z" />
                  </svg>
                </a>
              </li>
              <li className="ml-4">
                <a
                  href="https://www.instagram.com/filmustage/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex justify-center items-center text-purple-600 bg-gray-800 hover:text-gray-100 hover:bg-purple-600 rounded-full transition duration-150 ease-in-out"
                  aria-label="Instagram"
                  onClick={() => analyticsAction("landing_instagram_click")}
                >
                  <svg
                    className="w-8 h-8 fill-current"
                    viewBox="0 0 32 32"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="20.145" cy="11.892" r="1" />
                    <path d="M16 20c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4zm0-6c-1.103 0-2 .897-2 2s.897 2 2 2 2-.897 2-2-.897-2-2-2z" />
                    <path d="M20 24h-8c-2.056 0-4-1.944-4-4v-8c0-2.056 1.944-4 4-4h8c2.056 0 4 1.944 4 4v8c0 2.056-1.944 4-4 4zm-8-14c-.935 0-2 1.065-2 2v8c0 .953 1.047 2 2 2h8c.935 0 2-1.065 2-2v-8c0-.935-1.065-2-2-2h-8z" />
                  </svg>
                </a>
              </li>
              <li className="ml-4">
                <a
                  href="https://www.linkedin.com/company/filmustage/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex justify-center items-center text-purple-600 bg-gray-800 hover:text-gray-100 hover:bg-purple-600 rounded-full transition duration-150 ease-in-out"
                  aria-label="Linkedin"
                  onClick={() => analyticsAction("landing_linkedin_click")}
                >
                  <svg
                    className="w-8 h-8 fill-current"
                    viewBox="0 0 32 32"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M23.3 8H8.7c-.4 0-.7.3-.7.7v14.7c0 .3.3.6.7.6h14.7c.4 0 .7-.3.7-.7V8.7c-.1-.4-.4-.7-.8-.7zM12.7 21.6h-2.3V14h2.4v7.6h-.1zM11.6 13c-.8 0-1.4-.7-1.4-1.4 0-.8.6-1.4 1.4-1.4.8 0 1.4.6 1.4 1.4-.1.7-.7 1.4-1.4 1.4zm10 8.6h-2.4v-3.7c0-.9 0-2-1.2-2s-1.4 1-1.4 2v3.8h-2.4V14h2.3v1c.3-.6 1.1-1.2 2.2-1.2 2.4 0 2.8 1.6 2.8 3.6v4.2h.1z" />
                  </svg>
                </a>
              </li>
              <li className="ml-4">
                <a
                  href={`https://feedly.com/i/subscription/feed%2Fhttps%3A%2F%2Ffilmustage.com%2Frss.xml`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex justify-center items-center text-purple-600 bg-gray-800 hover:text-gray-100 hover:bg-purple-600 rounded-full transition duration-150 ease-in-out"
                  aria-label="Linkedin"
                  onClick={() => analyticsAction("landing_rss_click")}
                >
                  <svg
                    className="w-8 h-8 fill-current"
                    viewBox="-5.1 -4 34 34"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M13.86 1.989a2.609 2.609 0 0 0-3.726 0L.768 11.527a2.729 2.729 0 0 0 0 3.795l6.684 6.808a2.618 2.618 0 0 0 1.74.664h5.613a2.616 2.616 0 0 0 1.872-.791l6.554-6.675a2.726 2.726 0 0 0 0-3.795l-9.37-9.544zm-.26 17.422l-.935.95a.372.372 0 0 1-.268.114h-.8a.376.376 0 0 1-.247-.096l-.954-.97a.39.39 0 0 1 0-.542l1.337-1.36a.37.37 0 0 1 .531 0l1.337 1.361a.389.389 0 0 1 0 .543zm0-5.711l-3.737 3.808a.374.374 0 0 1-.268.111h-.799a.376.376 0 0 1-.25-.093l-.951-.97a.391.391 0 0 1 0-.544l4.139-4.214a.372.372 0 0 1 .531 0l1.337 1.362a.386.386 0 0 1 0 .54zm0-5.707l-6.54 6.66a.372.372 0 0 1-.268.113h-.8a.373.373 0 0 1-.249-.094L4.79 13.7a.388.388 0 0 1 0-.54l6.943-7.07a.372.372 0 0 1 .531 0l1.337 1.36a.389.389 0 0 1 0 .543z" />
                  </svg>
                </a>
              </li>
            </ul>
            {/* Copyrights note */}
            <div className="text-gray-400 text-sm mr-4">
              &copy; {new Date().getFullYear()} {t("footer.copyright")}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
